import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib/components/selects/CustomSelect/CustomSelect";
import { NumberInputField } from "@smartsuite/react-ui/lib/components/forms/NumberInputField/NumberInputField";
import { Paragraph } from "@smartsuite/react-ui/lib/components/typography/Paragraph/Paragraph";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import { isString } from "lodash";
import React, { memo, useCallback, useRef } from "react";
import { Application } from "../../api/types";
import { RenderContext } from "../../canvas/renderer/fabric-renderer";
import {
  hasModeAttrs,
  hasTextAttrs,
  hasWidthHeightAttrs,
} from "../../canvas/state/attributes";
import {
  DocumentDesignItem,
  FieldViewModes,
  LinkedRecordItem,
} from "../../canvas/state/types";
import { ColorPickerField } from "../ColorPickerField/ColorPickerField";
import { showElementWithoutImplementation } from "../utils";
import { LinkedRecordModeOptions } from "./constants";
import { DirectionsToggles } from "./DirectionsToggles/DirectionsToggles";

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { TableColumnSortableItem } from "./TableColumnSortableItem";
import { TextFieldInput } from "./TextFieldInput/TextFieldInput";
import {
  getLinkedRecordFieldSlugs,
  normalizeLinkedRecordTableFields,
} from "../../canvas/state/utils";
import { StyledSwitchButton } from "../StyledSwitchButton/StyledSwitchButton";
import { SidebarSelectSettingsButton } from "./SidebarSelectSettingsButton/SidebarSelectSettingsButton";

export interface SidebarSelectionConfigLinkedRecordProps {
  selected: LinkedRecordItem;
  context: RenderContext;
  onChangeSelected: (modifiedItem: DocumentDesignItem) => void;
}

const SidebarSelectionConfigLinkedRecordRaw: React.FC<
  SidebarSelectionConfigLinkedRecordProps
> = ({ selected, context, onChangeSelected }) => {
  const [optionSelectState, setOptionSelectState] = useSelectState();
  const sortBadgeValue = selected?.sort?.length ?? 0;
  const filterBadgeValue = selected?.filter?.fields?.length ?? 0;

  const [strokeWidthSelectState, setStrokeWidthSelectState] = useSelectState();
  const strokeWidthReference = useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const getFieldsFromLinkedApplication = useCallback((): Array<
    Record<string, string>
  > => {
    if (context.linkedApplications) {
      const linkedApplication: Application =
        context.linkedApplications[selected.field_linked_application];
      if (linkedApplication) {
        return linkedApplication.structure.map((field) => {
          return {
            id: field.slug,
            label: field.label,
            value: field.slug,
          };
        });
      }
    }
    return [];
  }, [context.linkedApplications, selected.field_linked_application]);

  const handleOpenSortModal = useCallback(
    () =>
      window.parent.postMessage(
        {
          type: "open-sorts",
          payload: {
            linkedApplicationId: selected.field_linked_application,
            initialSorts: selected.sort,
          },
        },
        "*",
      ),
    [selected.field_linked_application, selected.sort],
  );

  const handleOpenFilterModal = useCallback(
    () =>
      window.parent.postMessage(
        {
          type: "open-filters",
          payload: {
            linkedApplicationId: selected.field_linked_application,
            initialFilters: selected.filter,
          },
        },
        "*",
      ),
    [selected.field_linked_application, selected.filter],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const selectedLinkedRecordItem = selected;
        const fieldList = normalizeLinkedRecordTableFields(
          selectedLinkedRecordItem,
        );

        onChangeSelected({
          ...selectedLinkedRecordItem,
          field_list: arrayMove(
            fieldList,
            fieldList.findIndex((field) => field.field === active.id),
            fieldList.findIndex((field) => field.field === over.id),
          ),
        });
      }
    },
    [onChangeSelected, selected],
  );

  const handleSelectField = useCallback(
    (field: string) => {
      const fieldList = normalizeLinkedRecordTableFields(selected);
      fieldList.push({ field, width: 1 });

      onChangeSelected({ ...selected, field_list: fieldList });
    },
    [onChangeSelected, selected],
  );

  return (
    <>
      {hasWidthHeightAttrs(selected) && (
        <div className="sidebar-selection-config__section__split-column">
          <div>
            <TextFieldInput
              type="text"
              label="Width"
              value={`${(selected as LinkedRecordItem).width}`}
              onChangeValue={(value) =>
                onChangeSelected({
                  ...(selected as LinkedRecordItem),
                  width: parseInt(value),
                })
              }
            />
          </div>
          <div>
            <TextFieldInput
              type="text"
              label="Height"
              value={`${(selected as LinkedRecordItem).height}`}
              onChangeValue={(value) =>
                onChangeSelected({
                  ...(selected as LinkedRecordItem),
                  height: parseInt(value),
                })
              }
            />
          </div>
        </div>
      )}

      {hasModeAttrs(selected) && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Mode
            </Text>
          </div>
          <StyledSwitchButton
            options={LinkedRecordModeOptions}
            currentValue={selected.mode}
            onChange={(value) =>
              onChangeSelected({
                ...selected,
                mode: FieldViewModes[value as keyof typeof FieldViewModes],
              })
            }
          />
        </div>
      )}

      {showElementWithoutImplementation && (
        <div>
          <div style={{ marginBottom: 4 }}>
            <Text weight="medium" wrapMode="ellipsis">
              Sort
            </Text>
          </div>
          <Paragraph color="inactive" size="xs" className="text-margin">
            Sorting by order of records in the cell
          </Paragraph>
          <KeyValueCustomSelect options={[]} onChange={() => false} />
        </div>
      )}

      <div>
        <div style={{ marginBottom: 4 }}>
          <Text weight="medium" wrapMode="ellipsis">
            Linked Records Display
          </Text>
        </div>
        <Paragraph color="inactive" size="xs" className="text-margin">
          Define filter and sort your linked records
        </Paragraph>

        <div className="sidebar-button-filter-options">
          <SidebarSelectSettingsButton
            badgeValue={filterBadgeValue}
            label="Filter"
            iconName="filter"
            onClick={handleOpenFilterModal}
          />

          <SidebarSelectSettingsButton
            badgeValue={sortBadgeValue}
            label="Sort"
            iconName="sort"
            onClick={handleOpenSortModal}
          />
        </div>
      </div>

      {hasModeAttrs(selected) && selected.mode === FieldViewModes.table && (
        <>
          <div className="sidebar-selection-config__table_header">
            <header>
              <Text weight="medium" wrapMode="ellipsis">
                Table columns
              </Text>
              <Paragraph color="inactive" size="xs" className="text-margin">
                Set column widths based on relative proportions
              </Paragraph>
            </header>
            <ul>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={getLinkedRecordFieldSlugs(selected)}
                  strategy={verticalListSortingStrategy}
                >
                  {normalizeLinkedRecordTableFields(selected)
                    .map((field) => {
                      return getFieldsFromLinkedApplication().find(
                        (linkedField) => linkedField.value === field.field,
                      );
                    })
                    .map((field) => {
                      if (!field) return null;
                      return (
                        <TableColumnSortableItem
                          canRemoveItem={!!selected.field_list.length}
                          key={field.id}
                          id={field.id}
                          label={field.label}
                          slug={field.slug}
                          selected={selected}
                          onChangeSelected={onChangeSelected}
                        />
                      );
                    })}
                </SortableContext>
              </DndContext>
            </ul>

            <KeyValueCustomSelect
              placeholder="Add Column"
              state={optionSelectState}
              options={getFieldsFromLinkedApplication().filter((appField) => {
                return !selected.field_list.some((field) =>
                  isString(field)
                    ? field === appField.value
                    : field.field === appField.value,
                );
              })}
              onChange={handleSelectField}
              onOpenedChange={setOptionSelectState}
            />
          </div>

          <ColorPickerField
            label={"Border color"}
            value={selected.stroke}
            onChange={(value) =>
              onChangeSelected({ ...selected, stroke: value })
            }
          />

          <div style={{ marginBottom: 4 }}>
            <Text
              weight="medium"
              color={strokeWidthSelectState ? "link" : "primary"}
              wrapMode="ellipsis"
            >
              Border Width
            </Text>
          </div>

          <NumberInputField
            ref={strokeWidthReference}
            onFocus={() => setStrokeWidthSelectState(true)}
            onBlur={() => setStrokeWidthSelectState(false)}
            state={strokeWidthSelectState ? "active" : undefined}
            showSymbol={false}
            autoHideNavigation={true}
            inputStyles={{
              backgroundColor: "white",
              borderColor: "G-1-5",
              borderHoverColor: "G-3",
              borderFocusColor: "G-3",
            }}
            max="10"
            min="0"
            value={`${selected.strokeWidth ?? 0}`}
            onChangeValue={(value) => {
              if (strokeWidthReference.current)
                strokeWidthReference.current.focus();
              onChangeSelected({ ...selected, strokeWidth: parseInt(value) });
            }}
          />
        </>
      )}
      {showElementWithoutImplementation && <DirectionsToggles title="Border" />}

      {showElementWithoutImplementation && !hasTextAttrs(selected) && (
        <ColorPickerField
          label={"Border Color"}
          value={selected.stroke}
          onChange={(value) => onChangeSelected({ ...selected, stroke: value })}
        />
      )}

      {showElementWithoutImplementation && selected.fill !== "transparent" && (
        <div>
          <ColorPickerField
            label={hasTextAttrs(selected) ? "Text Color" : "Fill Color"}
            value={selected.fill}
            onChange={(value) => onChangeSelected({ ...selected, fill: value })}
          />
        </div>
      )}
    </>
  );
};

export const SidebarSelectionConfigLinkedRecord = memo(
  SidebarSelectionConfigLinkedRecordRaw,
);
